import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  CheckCircle,
  Loader2,
  FileText,
  Clock,
  Send,
  AlertCircle,
  GraduationCap,
  User,
  Briefcase,
  MessageSquare,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WrittenInterviewAnswer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/written-interview/answer/${token}`);
      if (!response.ok) throw new Error("Invalid or expired interview link");
      const result = await response.json();
      setData(result);
      // Pre-fill existing answers
      const existing = {};
      result.questions.forEach(q => {
        if (q.existing_answer) existing[q.question_id] = q.existing_answer;
      });
      setAnswers(existing);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const unanswered = data.questions.filter(q => !answers[q.question_id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = data.questions.map(q => ({
        question_id: q.question_id,
        answer_text: answers[q.question_id] || "",
      }));

      const formattedClarifications = data.pending_clarifications?.map(c => ({
        clarification_id: c.clarification_id,
        answer_text: clarificationAnswers[c.clarification_id] || "",
      })).filter(c => c.answer_text) || [];

      const response = await fetch(`${API_URL}/api/written-interview/answer/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers: formattedAnswers,
          clarification_answers: formattedClarifications,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to submit");
      }

      setSubmitted(true);
      toast.success("Answers submitted successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClarificationsOnly = async () => {
    const pending = data.pending_clarifications || [];
    const unanswered = pending.filter(c => !clarificationAnswers[c.clarification_id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} clarification questions`);
      return;
    }

    setSubmitting(true);
    try {
      const formatted = pending.map(c => ({
        clarification_id: c.clarification_id,
        answer_text: clarificationAnswers[c.clarification_id] || "",
      }));

      const response = await fetch(`${API_URL}/api/written-interview/answer/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers: data.questions.map(q => ({
            question_id: q.question_id,
            answer_text: q.existing_answer || answers[q.question_id] || "",
          })),
          clarification_answers: formatted,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to submit");
      }

      setSubmitted(true);
      toast.success("Clarification responses submitted!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md" data-testid="written-interview-link-expired">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid or Expired</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 text-left">
              <p className="font-semibold mb-1">Need a fresh link?</p>
              <p>Please reply to your invitation email or contact your RPL administrator and ask them to <span className="font-semibold">Resend the written interview link</span>. A new email will be issued instantly.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted || data.status === "answers_submitted" || data.status === "completed") {
    // If clarification_pending, don't show "submitted" — show clarification form instead
    if (data.status === "clarification_pending" && data.pending_clarifications?.length > 0) {
      // Fall through to clarification view below
    } else {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Answers Submitted</h2>
              <p className="text-slate-500">
                Thank you, {data.candidate_name}! Your written interview answers have been submitted.
                The external assessor will review them and you'll be notified of the outcome.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Clarification-only view: candidate only sees the questions needing clarification
  if (data.status === "clarification_pending" && data.pending_clarifications?.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Credentia AI</h1>
              <p className="text-xs text-slate-500">Clarification Required</p>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-slate-900">{data.candidate_name}</h2>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                {data.candidate_role && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    {data.candidate_role}
                  </span>
                )}
                {data.candidate_employer && (
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4 text-slate-400" />
                    {data.candidate_employer}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">The assessor needs clarification on {data.pending_clarifications.length} question(s)</p>
                  <p className="text-xs text-amber-600 mt-1">Please provide detailed responses below. Deadline: {data.deadline}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {data.pending_clarifications.map((c) => {
            // Find the original answer for context
            const originalQ = data.questions.find(q => q.question_id === c.question_id);
            return (
              <Card key={c.clarification_id} className="border-slate-200" data-testid={`clarification-card-${c.clarification_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {c.question_number}
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium text-slate-700">Original Question</CardTitle>
                      <p className="text-sm text-slate-600 mt-1">{c.original_question}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {originalQ?.existing_answer && (
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1 font-medium">Your Previous Answer:</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{originalQ.existing_answer}</p>
                    </div>
                  )}
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium mb-1">Assessor's Clarification Question:</p>
                    <p className="text-sm font-medium text-amber-800">{c.clarification_text}</p>
                  </div>
                  <Textarea
                    placeholder="Type your clarification response here..."
                    value={clarificationAnswers[c.clarification_id] || ""}
                    onChange={(e) => setClarificationAnswers({
                      ...clarificationAnswers,
                      [c.clarification_id]: e.target.value,
                    })}
                    rows={4}
                    data-testid={`clarification-answer-${c.clarification_id}`}
                  />
                </CardContent>
              </Card>
            );
          })}

          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Submit your clarifications</p>
                  <p className="text-sm text-slate-500">
                    {Object.values(clarificationAnswers).filter(v => v?.trim()).length} of {data.pending_clarifications.length} answered
                  </p>
                </div>
                <Button
                  onClick={handleSubmitClarificationsOnly}
                  disabled={submitting}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-clarifications-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Submit Clarifications</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const answeredCount = data.questions.filter(q => answers[q.question_id]?.trim()).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Credentia AI</h1>
              <p className="text-xs text-slate-500">Written Interview</p>
            </div>
          </div>
          <Badge className="bg-purple-100 text-purple-700 border border-purple-200">
            {answeredCount}/{data.questions.length} Answered
          </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Candidate Info Card */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="candidate-info-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{data.candidate_name}</h2>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                  {data.candidate_role && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      {data.candidate_role}
                    </span>
                  )}
                  {data.candidate_employer && (
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4 text-slate-400" />
                      {data.candidate_employer}
                    </span>
                  )}
                </div>
                {data.qualification && (
                  <p className="text-sm text-purple-600 mt-2">Qualification: {data.qualification}</p>
                )}
              </div>
            </div>
            
            {/* Deadline Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Deadline: {data.deadline}</p>
                <p className="text-xs text-amber-600">Please submit all answers before this date.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-800 mb-1">How to Complete Your Written Interview</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Answer each question with <strong>specific examples</strong> from your work experience</li>
                  <li>Be detailed — reference actual projects, certifications, and achievements</li>
                  <li>All 10 questions must be answered before submitting</li>
                  <li>An industry expert will review your answers and may send clarification questions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clarification Questions (if any) */}
        {data.pending_clarifications && data.pending_clarifications.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
                <MessageSquare className="w-4 h-4" />
                Clarification Questions ({data.pending_clarifications.length})
              </CardTitle>
              <CardDescription className="text-amber-600">
                The assessor needs more detail on some answers. Please respond below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.pending_clarifications.map((c) => (
                <div key={c.clarification_id} className="p-4 bg-white rounded-lg border border-amber-200">
                  <p className="text-xs text-slate-500 mb-1">Regarding Question #{c.question_number}:</p>
                  <p className="text-sm text-slate-600 italic mb-2">"{c.original_question}"</p>
                  <div className="bg-amber-50 p-3 rounded-md mb-3">
                    <p className="text-sm font-medium text-amber-800">
                      Assessor asks: {c.clarification_text}
                    </p>
                  </div>
                  <Textarea
                    placeholder="Your response to the clarification..."
                    value={clarificationAnswers[c.clarification_id] || ""}
                    onChange={(e) => setClarificationAnswers({
                      ...clarificationAnswers,
                      [c.clarification_id]: e.target.value,
                    })}
                    rows={3}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Questions */}
        {data.questions.map((q, idx) => (
          <Card key={q.question_id} className="border-slate-200" data-testid={`answer-card-${idx}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                  answers[q.question_id]?.trim()
                    ? "bg-green-100 text-green-700"
                    : "bg-purple-100 text-purple-700"
                }`}>
                  {answers[q.question_id]?.trim() ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                </div>
                <div>
                  <CardTitle className="text-sm font-medium text-slate-900">{q.question_text}</CardTitle>
                  <CardDescription className="text-xs mt-1">Related to: {q.competency_name}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Type your detailed answer here... Include specific examples from your experience."
                value={answers[q.question_id] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })}
                rows={5}
                className="resize-y"
                data-testid={`answer-textarea-${idx}`}
              />
              {answers[q.question_id]?.trim() && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Answered
                </p>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Submit Button */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Ready to submit?</p>
                <p className="text-sm text-slate-500">
                  {answeredCount === data.questions.length
                    ? "All questions answered. You can submit now."
                    : `${data.questions.length - answeredCount} question(s) still need answers.`}
                </p>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting || answeredCount < data.questions.length}
                size="lg"
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-answers-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Submit All Answers</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
